import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaList } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const ProcedureTypeCategories = () => {
  const { token } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState({
    id: null,
    name: '',
    description: '',
    is_active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    if (!token) return;
    try {
      const response = await apiCall('/scheduling/procedure-type-categories/', {
        method: 'GET'
      }, token);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Categories data:', data); // Debug log
        // Handle both paginated and non-paginated responses
        const categoriesArray = data.results || data;
        setCategories(Array.isArray(categoriesArray) ? categoriesArray : []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      alert('Error fetching categories');
      setCategories([]); // Set empty array on error
    }
  };

  const handleAddNew = () => {
    setCurrentCategory({ id: null, name: '', description: '', is_active: true });
    setIsModalOpen(true);
  };

  const handleEdit = (category) => {
    setCurrentCategory(category);
    setIsModalOpen(true);
  };

  const handleDelete = async (category) => {
    if (window.confirm(`Do you want to delete "${category.name}" category?`)) {
      try {
        const response = await apiCall(`/scheduling/procedure-type-categories/${category.id}/`, {
          method: 'DELETE'
        }, token);

        if (response.ok) {
          alert(`The "${category.name}" category was deleted successfully.`);
          fetchCategories();
        } else {
          const errorData = await response.json();
          alert(`Error: ${JSON.stringify(errorData)}`);
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Error deleting category');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentCategory.name) {
      alert('Name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const method = currentCategory.id ? 'PUT' : 'POST';
      const url = currentCategory.id
        ? `/scheduling/procedure-type-categories/${currentCategory.id}/`
        : '/scheduling/procedure-type-categories/';

      const response = await apiCall(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(currentCategory)
      }, token);

      if (response.ok) {
        alert(`The "${currentCategory.name}" category was ${currentCategory.id ? 'updated' : 'created'} successfully.`);
        setIsModalOpen(false);
        fetchCategories();
      } else {
        const errorData = await response.json();
        alert(`Error: ${JSON.stringify(errorData)}`);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error saving category');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
          <FaList className="text-primary" />
          Procedure Type Categories
        </h1>
        <button
          onClick={handleAddNew}
          className="btn btn-primary flex items-center gap-2"
        >
          <FaPlus /> Add New Category
        </button>
      </div>

      {/* Categories Table */}
      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.id}>
                <td className="font-medium">{category.name}</td>
                <td>{category.description || '-'}</td>
                <td>
                  {category.is_active ? (
                    <span className="badge badge-success">Active</span>
                  ) : (
                    <span className="badge badge-error">Inactive</span>
                  )}
                </td>
                <td>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className="btn btn-sm btn-ghost"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(category)}
                      className="btn btn-sm btn-ghost text-error"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              {currentCategory.id ? 'Edit' : 'Add'} Procedure Type Category
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text">Name *</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered"
                  value={currentCategory.name}
                  onChange={(e) => setCurrentCategory({ ...currentCategory, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text">Description</span>
                </label>
                <textarea
                  className="textarea textarea-bordered"
                  value={currentCategory.description}
                  onChange={(e) => setCurrentCategory({ ...currentCategory, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-control mb-4">
                <label className="cursor-pointer label">
                  <span className="label-text">Active</span>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={currentCategory.is_active}
                    onChange={(e) => setCurrentCategory({ ...currentCategory, is_active: e.target.checked })}
                  />
                </label>
              </div>

              <div className="modal-action">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
          <div className="modal-backdrop" onClick={() => !isSubmitting && setIsModalOpen(false)}></div>
        </div>
      )}
    </div>
  );
};

export default ProcedureTypeCategories;
